/**
 * Hala CRM - My Requests Page
 * Shows requests owned/assigned to the current user
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ClipboardList, Search, RefreshCw, User, ChevronRight, Clock,
  CheckCircle, AlertCircle, FileText, Inbox, UserCheck, XCircle, Play
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const STATUS_CONFIG = {
  NEW: { label: 'جديد', color: COLORS.info, bg: COLORS.infoLight, icon: FileText },
  IN_POOL: { label: 'في المجمع', color: COLORS.warning, bg: COLORS.warningLight, icon: Inbox },
  ASSIGNED: { label: 'مُسند', color: COLORS.primary, bg: `${COLORS.primary}20`, icon: UserCheck },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bg: `${COLORS.gold}20`, icon: Clock },
  COMPLETED: { label: 'مكتمل', color: COLORS.success, bg: COLORS.successLight, icon: CheckCircle },
  CANCELLED: { label: 'ملغي', color: COLORS.error, bg: COLORS.errorLight, icon: XCircle },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, color: COLORS.textMuted, bg: COLORS.bgDark, icon: AlertCircle };
  const Icon = config.icon;
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      borderRadius: '20px',
      backgroundColor: config.bg,
      color: config.color,
      fontSize: '12px',
      fontWeight: 500,
    }}>
      <Icon size={14} />
      {config.label}
    </span>
  );
}

export default function CRMMyRequestsPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, completed
  const [search, setSearch] = useState('');

  const fetchMyRequests = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      const res = await axios.get(
        `${API}/requests/my/${user.user_id}`,
        { headers: getCRMHeaders() }
      );
      setRequests(res.data || []);
    } catch (e) {
      console.error('Failed to fetch my requests:', e);
    } finally {
      setLoading(false);
    }
  }, [user, getCRMHeaders]);

  useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Filter requests
  let filteredRequests = requests;
  if (filter === 'active') {
    filteredRequests = requests.filter(r => !['COMPLETED', 'CANCELLED'].includes(r.status));
  } else if (filter === 'completed') {
    filteredRequests = requests.filter(r => ['COMPLETED', 'CANCELLED'].includes(r.status));
  }

  // Search filter
  if (search) {
    filteredRequests = filteredRequests.filter(r =>
      r.applicant_name?.includes(search) ||
      r.service_name_ar?.includes(search) ||
      r.mobile?.includes(search) ||
      r.request_id?.includes(search)
    );
  }

  // Stats
  const stats = {
    total: requests.length,
    active: requests.filter(r => !['COMPLETED', 'CANCELLED'].includes(r.status)).length,
    completed: requests.filter(r => r.status === 'COMPLETED').length,
    inProgress: requests.filter(r => r.status === 'IN_PROGRESS').length,
  };

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
          طلباتي
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
          الطلبات المُسندة إليك ({stats.total} طلب)
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div
          style={{
            backgroundColor: filter === 'all' ? COLORS.primary : COLORS.white,
            padding: '16px',
            borderRadius: '12px',
            border: `1px solid ${filter === 'all' ? COLORS.primary : COLORS.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => setFilter('all')}
        >
          <p style={{ margin: 0, fontSize: '12px', color: filter === 'all' ? 'rgba(255,255,255,0.8)' : COLORS.textMuted }}>
            إجمالي الطلبات
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '24px', fontWeight: 700, color: filter === 'all' ? COLORS.white : COLORS.text }}>
            {stats.total}
          </p>
        </div>
        
        <div
          style={{
            backgroundColor: filter === 'active' ? COLORS.info : COLORS.white,
            padding: '16px',
            borderRadius: '12px',
            border: `1px solid ${filter === 'active' ? COLORS.info : COLORS.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => setFilter('active')}
        >
          <p style={{ margin: 0, fontSize: '12px', color: filter === 'active' ? 'rgba(255,255,255,0.8)' : COLORS.textMuted }}>
            طلبات نشطة
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '24px', fontWeight: 700, color: filter === 'active' ? COLORS.white : COLORS.info }}>
            {stats.active}
          </p>
        </div>
        
        <div style={{
          backgroundColor: COLORS.white,
          padding: '16px',
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Play size={16} color={COLORS.gold} />
            <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>قيد التنفيذ</p>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '24px', fontWeight: 700, color: COLORS.gold }}>
            {stats.inProgress}
          </p>
        </div>
        
        <div
          style={{
            backgroundColor: filter === 'completed' ? COLORS.success : COLORS.white,
            padding: '16px',
            borderRadius: '12px',
            border: `1px solid ${filter === 'completed' ? COLORS.success : COLORS.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => setFilter('completed')}
        >
          <p style={{ margin: 0, fontSize: '12px', color: filter === 'completed' ? 'rgba(255,255,255,0.8)' : COLORS.textMuted }}>
            مكتملة
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '24px', fontWeight: 700, color: filter === 'completed' ? COLORS.white : COLORS.success }}>
            {stats.completed}
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="بحث بالاسم أو الخدمة أو رقم الطلب..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px 10px 40px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              textAlign: 'right',
            }}
          />
        </div>
        <button
          onClick={fetchMyRequests}
          style={{
            padding: '10px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={18} color={COLORS.textMuted} />
        </button>
      </div>

      {/* Requests List */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            جاري التحميل...
          </div>
        ) : filteredRequests.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <ClipboardList size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.textMuted} />
            <p style={{ margin: 0, color: COLORS.textMuted }}>
              {search ? 'لا توجد نتائج' : 'لا توجد طلبات مُسندة إليك'}
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>رقم الطلب</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>مقدم الطلب</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الخدمة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>التاريخ</th>
                <th style={{ padding: '14px 16px', textAlign: 'left' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req) => (
                <tr
                  key={req.request_id}
                  style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer' }}
                  onClick={() => navigate(`/crm/request/${req.request_id}`)}
                  data-testid={`request-row-${req.request_id}`}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.primary }}>
                      {req.request_id?.substring(0, 16)}...
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: COLORS.bgDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <User size={16} color={COLORS.textMuted} />
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                          {req.applicant_name || 'غير محدد'}
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>
                          {req.mobile}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', color: COLORS.text }}>
                      {req.service_name_ar || 'غير محدد'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <StatusBadge status={req.status} />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
                      {formatDate(req.created_at)}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'left' }}>
                    <ChevronRight size={18} color={COLORS.textMuted} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
